import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTasks } from '../../context/TasksContext';
import { TaskList } from '../../types/tasks';
import ListCard from './ListCard';
import ShareDialog from './ShareDialog';
import TaskDialog from './TaskDialog';

/**
 * Mounted once in App: shows one list in a dialog — the list a linked
 * week-list line comes from, opened from its "+N" or purple chip, with that
 * line pulsing — without it crowding the Lists page of people who aren't on it.
 *
 * Who can work it matches the server (editableListSql in api/utils/tasks/core.ts):
 * its owner and the people it's shared with, plus every admin for a warehouse
 * request. Everyone else who sees the week list gets it read-only.
 */
export const ListDialogHost: React.FC = () => {
  const { listDialog, closeListDialog, lists, itemsForList, members, deleteList, canEditList } = useTasks();
  const [shareTarget, setShareTarget] = useState<TaskList | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);

  const list = listDialog ? lists.find((l) => l.id === listDialog.listId) : undefined;
  if (!listDialog) return null;
  const canWork = !!list && canEditList(list);

  return (
    <>
      <TaskDialog title={list?.title || 'List'} onClose={closeListDialog} wide>
        {list ? (
          <div className="tl-dialog-card">
            {!canWork && (
              <p className="tk-muted tl-dialog-note">
                Read-only — it's here because a task from it is on this week's list. Only the people it's shared with
                can change it.
              </p>
            )}
            <ListCard
              readOnly={!canWork}
              list={list}
              items={itemsForList(list.id)}
              members={members.get(list.id) || []}
              highlightIds={new Set()}
              flashId={listDialog.itemId}
              mineOnly={false}
              search=""
              onOpenImage={setLightbox}
              onShare={setShareTarget}
              onDelete={(l) => {
                if (window.confirm(`Delete "${l.title || 'Untitled list'}"? It's removed for everyone it's shared with.`)) {
                  deleteList(l.id);
                  closeListDialog();
                }
              }}
            />
          </div>
        ) : (
          <p className="tk-muted">
            You can't open this list — it's private to the people it's shared with, or it's been archived or deleted.
            Ticking the line on the week list still ticks everything under it.
          </p>
        )}
      </TaskDialog>
      {shareTarget && (
        <ShareDialog list={shareTarget} members={members.get(shareTarget.id) || []} onClose={() => setShareTarget(null)} />
      )}
      {lightbox &&
        createPortal(
          <div className="tk-lightbox" onClick={() => setLightbox(null)} role="dialog" aria-label="Photo">
            <img src={lightbox} alt="" />
          </div>,
          document.body
        )}
    </>
  );
};

export default ListDialogHost;
